/**
 * Auto-opens the Properties Panel's Object/Type/Photo sections to match the current selection
 * (see `computePropertiesPanelSelectionSignature` for the selection -> section rules), without
 * fighting a user's explicit open/close choice for a selection already encountered.
 *
 * Driven entirely by `selectionKey` transitions: a re-render with the same key is a no-op, so
 * this never re-runs on every render, property update, keystroke, or panel interaction — only on
 * a meaningful selection change.
 *
 * Every selection's open sections (all five) are remembered when it's left, and restored exactly
 * whenever it's selected again — directly from another object or after a deselect. A selection
 * seen for the first time opens only its own section of Object/Type/Photo (Type for text, Photo
 * for a photo, Object otherwise), unless the user's latest action on that section collapsed it;
 * Layout/Effects stay as they are. Clearing the selection also resets the live state, since the
 * panel shows everything collapsed while nothing is selected (see `useSelectToolSections`), so a
 * new selection after it never inherits the sections a different, previously selected object
 * had open.
 *
 * Per-selection memory is a plain in-memory map keyed by the opaque `selectionKey`, scoped to
 * this hook instance's lifetime — intentionally minimal so a later ticket can formalize it (e.g.
 * persist per-object across tool switches or reloads) without this call site changing shape.
 */
import { useEffect, useRef } from 'react'
import type { PropertiesPanelDefaultSectionId } from '@/lib/panels/types/properties-panel-default-section-id'

const AUTO_OPEN_SECTION_IDS = ['object', 'type', 'photo'] as const

function isAutoOpenSectionId(id: string): id is PropertiesPanelDefaultSectionId {
  return (AUTO_OPEN_SECTION_IDS as readonly string[]).includes(id)
}

export interface UsePropertiesPanelAutoOpenSectionInput {
  /** Opaque per-selection identity from `computePropertiesPanelSelectionSignature`; `null` when nothing is selected. */
  selectionKey: string | null
  /** The section this selection should open the first time it's encountered; `null` when nothing is selected. */
  defaultSectionId: PropertiesPanelDefaultSectionId | null
  /** Currently open section ids (any of Object/Type/Photo/Layout/Effects). */
  openSectionIds: readonly string[]
  /** Reconciles the full open-section-id set — same shape as an Accordion `onValueChange`. */
  setOpenSectionIds: (ids: string[]) => void
  /** The user's own latest open (`true`) / close (`false`) of a section, if any. A first-time
   *  selection doesn't auto-open a section whose latest explicit action was a close. */
  getExplicitSectionState: (id: PropertiesPanelDefaultSectionId) => boolean | undefined
}

export function usePropertiesPanelAutoOpenSection({
  selectionKey,
  defaultSectionId,
  openSectionIds,
  setOpenSectionIds,
  getExplicitSectionState,
}: UsePropertiesPanelAutoOpenSectionInput): void {
  const memoryRef = useRef(new Map<string, readonly string[]>())
  const lastSelectionKeyRef = useRef<string | null>(null)
  const openSectionIdsRef = useRef(openSectionIds)
  // The open ids as of the last commit, written by the effect declared *after* the transition
  // effect below, so the transition effect reads the state the user actually left the previous
  // selection in. `openSectionIdsRef` can't be used for that: it already includes anything
  // opened in the same batch as the selection change, which would otherwise be remembered as the
  // user's choice.
  const settledOpenSectionIdsRef = useRef(openSectionIds)

  openSectionIdsRef.current = openSectionIds

  useEffect(() => {
    const previousKey = lastSelectionKeyRef.current

    lastSelectionKeyRef.current = selectionKey

    // Not a selection transition (including a same-selection re-render) — leave every section
    // exactly as the user (or a prior run of this effect) left it.
    if (previousKey === selectionKey) return

    const currentIds = openSectionIdsRef.current

    // Remember exactly how the selection we're leaving was left, so returning to it later
    // restores its own state instead of refighting the user's choice for it.
    if (previousKey != null) {
      memoryRef.current.set(previousKey, settledOpenSectionIdsRef.current)
    }

    if (selectionKey == null) {
      // Entering a no-selection stretch (see this hook's doc comment). Nothing is lost: the
      // selection being left was just remembered above.
      if (previousKey != null && currentIds.length > 0) setOpenSectionIds([])

      return
    }

    const remembered = memoryRef.current.get(selectionKey)

    if (remembered) {
      // A selection we've already visited (never re-run the first-time default-open for it):
      // restore all five sections exactly as the user left it, however it's reached.
      setOpenSectionIds([...remembered])
      return
    }

    // First time this exact selection has been seen: of Object/Type/Photo, open only its own
    // section (Type for text, Photo for a photo, Object otherwise) — never one a different,
    // previously selected object had open, and never one the user's latest action collapsed
    // (it stays collapsed until they open it again). Layout/Effects are left as they are.
    const untouched = currentIds.filter((id) => !isAutoOpenSectionId(id))
    const next =
      defaultSectionId && getExplicitSectionState(defaultSectionId) !== false
        ? [...untouched, defaultSectionId]
        : untouched

    if (next.length !== currentIds.length || next.some((id) => !currentIds.includes(id))) {
      setOpenSectionIds(next)
    }
  }, [selectionKey, defaultSectionId, setOpenSectionIds, getExplicitSectionState])

  // Must stay declared after the transition effect above (see `settledOpenSectionIdsRef`).
  useEffect(() => {
    settledOpenSectionIdsRef.current = openSectionIds
  }, [openSectionIds])
}
